import type jsPDF from 'jspdf';
import {
    generateAushangPdf,
    aushangDateiname,
    type AushangDaten,
    type AushangFormat,
} from '../../services/pdf/generatorAushang';
import {
    generateHandbuchPdf,
    handbuchDateiname,
    type HandbuchPdfDaten,
    type HandbuchProjektAngaben,
} from '../../services/pdf/generatorHandbuch';
import type { ProjectGlobalData, ReportMetadata } from '../../types';
import type { Gefaehrdung, Schutzmassnahme } from '../sige-plan/types';
import type { Handbuch } from './types';

/**
 * Anschluss des Handbuchmoduls an werfende Nachbarn (jsPDF).
 *
 * Muster wie `planungsphase/planungIntegration.ts` und
 * `sige-einweisung/einweisungIntegration.ts`: die Komponente soll nicht in
 * einem `try` stehen, und ein einzelnes kaputtes Feld darf nicht das ganze
 * Modul weissbrennen.
 *
 * Hier liegt ausserdem die EINZIGE Stelle, die `ReportMetadata` auf die
 * Eingabeform der Generatoren abbildet. Absicht: `src/types.ts` ist der
 * Bug-Magnet dieses Repositoriums; die Generatoren sollen davon nichts
 * wissen und ohne den Projektzweig testbar bleiben. Wenn sich ein Feldname
 * dort aendert, ist genau diese Datei zu aendern und sonst nichts.
 *
 * Der Diskriminator ist bewusst eine ZEICHENKETTE und kein boolean: das
 * Projekt uebersetzt ohne `strictNullChecks`, und ein `{ ok: false }` liesse
 * sich dort versehentlich auf einem Objekt lesen, das gar keinen `ok`-
 * Schluessel hat.
 */
export type Anschluss<T> =
    | { status: 'ok'; wert: T }
    | { status: 'fehler'; fehler: string };

const alsFehler = (e: unknown, was: string): { status: 'fehler'; fehler: string } => ({
    status: 'fehler',
    fehler: `${was}: ${e instanceof Error ? e.message : String(e)}`,
});

const text = (v?: string | null): string => (v == null ? '' : String(v)).trim();

/** Die Personen aus dem Verteiler, auf das eingedampft, was aufs Blatt kommt. */
const beteiligteAus = (metadata?: Partial<ReportMetadata>): HandbuchProjektAngaben['beteiligte'] =>
    (metadata?.distributionList || [])
        .filter(k => text(k?.name) || text(k?.company))
        .map(k => ({
            rolle: text(k.role),
            name: text(k.name),
            firma: text(k.company),
            telefon: text(k.phone),
            gewerk: text(k.tradeType),
        }));

/**
 * Projektstammdaten fuer das Handbuch-PDF.
 *
 * `siteContact*` ist die Bauleitung vor Ort, `contractorCompany`/`contractorName`
 * der beauftragte Unternehmer — beides sind eigene Felder in `ReportMetadata`
 * und keine Rollen im Verteiler. Sie werden deshalb ausdruecklich gelesen und
 * nicht aus `distributionList` geraten.
 */
export const projektAngabenAus = (
    metadata?: Partial<ReportMetadata>,
    globalData?: Partial<ProjectGlobalData>,
    plan?: { planNr?: string | null; planVersion?: string | null },
): HandbuchProjektAngaben => ({
    projektName: text(metadata?.projectName),
    projektNummer: text(metadata?.projectNumber),
    projektAdresse: text(metadata?.projectAddress),
    bauherrName: text(metadata?.clientName),
    bauherrAdresse: text(metadata?.clientAddress),
    bauherrKontakt: text(metadata?.clientContactPerson),
    bauherrTelefon: text(metadata?.clientPhone),
    koordinatorPerson: text(metadata?.coordinator),
    // Die Firma des Koordinators steht nicht in `metadata` — sie ist die
    // eigene Firma und kommt aus dem Profil. Der Aufrufer reicht sie nach,
    // wenn er sie hat; geraten wird sie hier nicht.
    koordinatorFirma: '',
    koordinatorTelefon: text(metadata?.coordinatorPhone),
    bauleitungFirma: text(metadata?.siteContactCompany),
    bauleitungPerson: text(metadata?.siteContactPerson),
    bauleitungTelefon: text(metadata?.siteContactPhone),
    auftragnehmer: [text(metadata?.contractorCompany), text(metadata?.contractorName)]
        .filter(Boolean).join(' — '),
    bauzeitVon: text(globalData?.startDate),
    bauzeitBis: text(globalData?.endDate),
    planNr: text(plan?.planNr),
    planVersion: text(plan?.planVersion),
    anzahlLageplaene: (metadata?.plans || []).length,
    beteiligte: beteiligteAus(metadata),
});

/**
 * Gruppiert eine flache Liste nach einem Fremdschluessel.
 *
 * `useHandbuch` liefert Gefaehrdungen und Massnahmen als FLACHE Listen (so
 * kommen sie aus `ladePlanQuelle`), die Generatoren erwarten sie nach ihrem
 * Bezug geordnet. Die Umformung steht hier und nicht im Generator: der
 * Generator soll ohne den Projektzweig testbar bleiben.
 */
const gruppiere = <T>(liste: T[] | null | undefined, schluessel: (e: T) => string | undefined | null): Record<string, T[]> => {
    const nach: Record<string, T[]> = {};
    (liste || []).forEach(e => {
        const k = schluessel(e);
        if (!k) return;
        (nach[k] = nach[k] || []).push(e);
    });
    return nach;
};

/** Gefaehrdungen je Arbeitsschritt-id. */
export const gefaehrdungenJeSchritt = (liste?: Gefaehrdung[] | null): Record<string, Gefaehrdung[]> =>
    gruppiere(liste, g => g?.arbeitsschritt_id);

/** Schutzmassnahmen je Gefaehrdungs-id. */
export const massnahmenJeGefaehrdung = (liste?: Schutzmassnahme[] | null): Record<string, Schutzmassnahme[]> =>
    gruppiere(liste, m => m?.gefaehrdung_id);

/**
 * Die Angaben des Aushangs.
 *
 * `ordnung` und `notrufplan` werden DURCHGEREICHT und nicht ergaenzt: der
 * Aushang erfasst keine Notfalldaten. Fehlt der Notrufplan, sagt das Blatt
 * das — hier eine Ersatzadresse einzusetzen waere genau der Fehler, den die
 * Entscheidung ausschliesst (zwei Krankenhaeuser im selben Projekt).
 */
export const aushangDatenAus = (
    metadata: Partial<ReportMetadata> | undefined,
    handbuch: Handbuch | null,
    notrufplan: AushangDaten['notrufplan'],
    plan?: { planNr?: string | null; standIso?: string | null },
): AushangDaten => ({
    projektName: text(metadata?.projectName),
    projektAdresse: text(metadata?.projectAddress),
    bauherrName: text(metadata?.clientName),
    bauherrTelefon: text(metadata?.clientPhone),
    bauleitungFirma: text(metadata?.siteContactCompany),
    bauleitungPerson: text(metadata?.siteContactPerson),
    bauleitungTelefon: text(metadata?.siteContactPhone),
    koordinatorPerson: text(metadata?.coordinator),
    koordinatorTelefon: text(metadata?.coordinatorPhone),
    ordnung: handbuch ? handbuch.ordnung : null,
    notrufplan: notrufplan || null,
    standIso: text(plan?.standIso),
    fassung: text(handbuch?.version_label),
    planNr: text(plan?.planNr),
});

/** Erzeugt das Handbuch als A3-Dokument. */
export const baueHandbuchPdf = (daten: HandbuchPdfDaten): Anschluss<jsPDF> => {
    try {
        return { status: 'ok', wert: generateHandbuchPdf(daten) };
    } catch (e) {
        return alsFehler(e, 'Das Handbuch konnte nicht erzeugt werden');
    }
};

/** Erzeugt den Aushang. Standard A3 hoch, A2 auf Wunsch — kein A4. */
export const baueAushangPdf = (
    daten: AushangDaten,
    format: AushangFormat = 'a3',
): Anschluss<jsPDF> => {
    try {
        return { status: 'ok', wert: generateAushangPdf(daten, { format }) };
    } catch (e) {
        return alsFehler(e, 'Der Aushang konnte nicht erzeugt werden');
    }
};

/** Speichert das erzeugte Dokument. Getrennt vom Erzeugen, damit der Aufrufer
 *  dasselbe Blatt auch anzeigen oder ablegen kann. */
export const speicherePdf = (doc: jsPDF, dateiname: string): Anschluss<true> => {
    try {
        doc.save(dateiname);
        return { status: 'ok', wert: true };
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht gespeichert werden');
    }
};

/** Bytes fuer die Ablage im Dokumentenmanagement bzw. den Versand. */
export const pdfAlsBlob = (doc: jsPDF): Anschluss<Blob> => {
    try {
        return { status: 'ok', wert: doc.output('blob') as Blob };
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht ausgegeben werden');
    }
};

export { aushangDateiname, handbuchDateiname };
/** Durchgereicht, damit der Aufrufer nur EINE Datei kennen muss. */
export type { AushangDaten, AushangFormat, HandbuchPdfDaten, HandbuchProjektAngaben };
